import { type ReactNode, createContext, useContext, useEffect } from 'react';

/**
 * WAS EINE MASKE IN DEN SEITENKOPF MELDET (ÄA-41).
 *
 * Der Seitenkopf gehört dem Rahmen; manches darin weiß aber nur die Maske:
 * der Zähler einer Liste („128 Angebote") und die Handlungen einer Erfassung,
 * die oben rechts UND am Ende stehen sollen. Beides meldet die Maske über
 * diesen Kontext — ohne dass die Arbeitsfläche je Maske eine eigene Leitung
 * legt. Beim Verlassen der Maske fällt es weg.
 */
export type Seitenzusatz = {
  readonly zaehler?: string | undefined;
  readonly handlungen?: ReactNode;
};

type Melder = (teil: Seitenzusatz) => void;

const Kontext = createContext<Melder>(() => undefined);

export const SeitenkontextAnbieter = Kontext.Provider;

/**
 * Der Zähler im Seitenkopf. `undefined` nimmt ihn zurück — etwa, solange die
 * Liste noch lädt; ein Zähler „0" in dieser Zeit wäre eine falsche Auskunft.
 */
export function useZaehler(text: string | undefined): void {
  const melde = useContext(Kontext);
  useEffect(() => {
    melde({ zaehler: text });
    return () => melde({ zaehler: undefined });
  }, [melde, text]);
}

/**
 * Handlungen im Seitenkopf. Die Abhängigkeiten nennt der Aufrufer: Ein
 * Knoten ist bei jedem Bild ein neues Objekt, und wer ihn selbst als
 * Abhängigkeit nähme, setzte den Kopf ohne Ende neu.
 */
export function useKopfhandlungen(knoten: ReactNode, abhaengig: readonly unknown[]): void {
  const melde = useContext(Kontext);
  useEffect(() => {
    melde({ handlungen: knoten });
    return () => melde({ handlungen: undefined });
  }, [melde, ...abhaengig]);
}

/** Ein Zähler als Wort: „1 Angebot", „12 Angebote", „mehr als 500 Kunden". */
export function zaehlwort(
  anzahl: number | undefined,
  einzahl: string,
  mehrzahl: string,
  mehrAlsGezeigt = false,
): string | undefined {
  if (anzahl === undefined) return undefined;
  if (mehrAlsGezeigt) return `mehr als ${anzahl} ${mehrzahl}`;
  return `${anzahl} ${anzahl === 1 ? einzahl : mehrzahl}`;
}
